import "./style.css";
import LeftSide from "./components/LeftSide";
import RightSideCars from "./components/RightSideCars.jsx";
import { fetchQuote } from "../api/api.js";
import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  setServiceType,
  setPickup,
  setDropoff,
  setDate,
  setTime,
  setPassenger,
  setLuggage,
  setBooster,
  setRegular,
  setInfant,
  setDistance,
  setDuration,
} from "../features/bookingSlice/bookingSlice";
import { useNavigate } from "react-router-dom";
import PremiumLoader from "../components/PremiumLoader";
import { useJsApiLoader } from "@react-google-maps/api";

const MAP_LIBRARIES = ['places'];

const QuotePage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const bookingData = useSelector((state) => state.booking);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries: MAP_LIBRARIES,
  });

  const extractQueryParams = () => {
    const params = new URLSearchParams(window.location.search);
    return {
      service_type: params.get("service_type") || "from_airport",
      pickup: {
        address: params.get("from") || "Boston Logan International Airport",
        coordinates: {
          lat: parseFloat(params.get("start_lat")) || 0,
          lng: parseFloat(params.get("start_lng")) || 0,
        },
      },
      dropoff: {
        address: params.get("to") || "Boston Logan International Airport",
        coordinates: {
          lat: parseFloat(params.get("end_lat")) || 0,
          lng: parseFloat(params.get("end_lng")) || 0,
        },
      },
      date: (() => {
        const rawDate = params.get("pick_date") || "";
        if (!rawDate) return "";
        const str = rawDate.trim();
        // Handle YYYY-MM-DD or DD-MM-YYYY
        if (str.includes('-')) {
          const parts = str.split('-');
          if (parts.length === 3) {
            if (parts[0].length === 4) {
              // YYYY-MM-DD -> MM/DD/YYYY
              return `${parts[1]}/${parts[2]}/${parts[0]}`;
            } else if (parts[2].length === 4) {
              // DD-MM-YYYY -> MM/DD/YYYY
              return `${parts[1]}/${parts[0]}/${parts[2]}`;
            }
          }
        }
        // Handle DD/MM/YYYY
        if (str.includes('/')) {
          const parts = str.split('/');
          if (parts.length === 3 && parseInt(parts[0], 10) > 12) {
            return `${parts[1]}/${parts[0]}/${parts[2]}`;
          }
        }
        return str;
      })(),
      time: params.get("pick_time") || "",
      passenger: Number(params.get("passengers")) || 1,
      luggage: Number(params.get("luggage")) || 1,
      infant: params.get("infant_seat") || "0",
      regular: params.get("regular_seat") || "0",
      booster: params.get("booster_seat") || "0",
    };
  };

  const updateReduxWithQueryParams = (queryData) => {
    dispatch(setServiceType(queryData.service_type));
    dispatch(setPickup(queryData.pickup));
    dispatch(setDropoff(queryData.dropoff));
    dispatch(setDate(queryData.date));
    dispatch(setTime(queryData.time));
    dispatch(setPassenger(queryData.passenger));
    dispatch(setLuggage(queryData.luggage));
    dispatch(setBooster(queryData.booster));
    dispatch(setRegular(queryData.regular));
    dispatch(setInfant(queryData.infant));
  };

  useEffect(() => {
    const hasRequiredBookingData =
      bookingData.pickup?.address &&
      bookingData.dropoff?.address &&
      bookingData.date &&
      bookingData.time;

    if (!hasRequiredBookingData) {
      const queryData = extractQueryParams();
      if (queryData.pickup.address && queryData.dropoff.address) {
        updateReduxWithQueryParams(queryData);
      } else {
        navigate("/");
        return;
      }
    }

    const currentBookingData = hasRequiredBookingData
      ? bookingData
      : extractQueryParams();

    setLoading(true);

    // Check if we have an instant pre-fetch from index.html
    const prefetch = window.__PREFETCHED_QUOTE__;
    const quotePromise = (prefetch && prefetch.promise)
      ? prefetch.promise
      : fetchQuote(currentBookingData);

    quotePromise
      .then((res) => {
        console.log("fetchQuote response (prefetch/live):", res);
        const quoteData = res?.data?.fleets ? res.data : res;
        setData(quoteData);
        setLoading(false);
      })
      .catch((err) => {
        console.warn("Prefetch or direct fetch error, retrying standard fetch:", err);
        // Fallback to regular fetchQuote if prefetch failed
        if (prefetch && prefetch.promise) {
          window.__PREFETCHED_QUOTE__ = null;
          fetchQuote(currentBookingData)
            .then((res) => {
              const quoteData = res?.data?.fleets ? res.data : res;
              setData(quoteData);
              setLoading(false);
            })
            .catch((retryErr) => {
              const errorMsg = retryErr.response?.data?.error?.message 
                || retryErr.response?.data?.message 
                || retryErr.message 
                || "Failed to fetch corporate fleet rates. Please check route details.";
              setError(errorMsg);
              setLoading(false);
            });
        } else {
          const errorMsg = err.response?.data?.error?.message 
            || err.response?.data?.message 
            || err.message 
            || "Failed to fetch corporate fleet rates. Please check route details.";
          setError(errorMsg);
          setLoading(false);
        }
      });
  }, []);

  const { pickup, dropoff } = bookingData;

  useEffect(() => {
    if (isLoaded && pickup?.coordinates && dropoff?.coordinates && window.google) {
      const directionsService = new window.google.maps.DirectionsService();
      directionsService.route(
        {
          origin: pickup.coordinates,
          destination: dropoff.coordinates,
          travelMode: window.google.maps.TravelMode.DRIVING,
          unitSystem: window.google.maps.UnitSystem.IMPERIAL,
          region: 'US'
        },
        (result, status) => {
          if (status === "OK" && result.routes[0]?.legs[0]) {
            dispatch(setDistance(result.routes[0].legs[0].distance.text));
            dispatch(setDuration(result.routes[0].legs[0].duration.text));
          } else {
            dispatch(setDistance("N/A"));
            dispatch(setDuration("N/A"));
          }
        }
      );
    }
  }, [isLoaded, pickup, dropoff, dispatch]);

  return (
    <div className="min-h-screen bg-[#070b14] text-slate-100 py-8 px-4 sm:px-6 lg:px-8">
      {loading && (
        <div className="fixed inset-0 z-50 bg-slate-950/80 backdrop-blur-md flex flex-col items-center justify-center">
          <PremiumLoader />
          <p className="mt-4 text-xs font-bold uppercase tracking-widest text-blue-400">Loading Available Chauffeurs...</p>
        </div>
      )}

      <div className="max-w-[1480px] mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          {/* Left Column: Itinerary Ticket Card */}
          <div className="lg:col-span-4 lg:sticky lg:top-24 rounded-2xl bg-slate-900/90 border border-slate-800 p-5 sm:p-6 backdrop-blur-xl shadow-2xl">
            <LeftSide />
          </div>

          {/* Right Column: Fleet Grid */}
          <div className="lg:col-span-8 rounded-2xl bg-slate-900/60 border border-slate-800/80 p-5 sm:p-8 backdrop-blur-xl shadow-2xl">
            <RightSideCars data={data} error={error} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuotePage;
